'use client'

import { createContext, useContext, useState, useCallback } from 'react'

const TopicContext = createContext(null)

export function TopicProvider({ children, subtopics = [], allArticles = [] }) {
  // Active section: null = show all, or { id, name, articles }
  const [activeSection, setActiveSection] = useState(null)

  // Highlighted article ID (for hover effects)
  const [highlightedArticleId, setHighlightedArticleId] = useState(null)

  // Bias filters: which bias categories to show
  const [biasFilters, setBiasFilters] = useState(['left', 'center', 'right'])

  // Mobile modal state
  const [mobileModalOpen, setMobileModalOpen] = useState(false)
  const [mobileModalArticles, setMobileModalArticles] = useState([])
  const [mobileModalTitle, setMobileModalTitle] = useState('')

  const filterBySection = useCallback((sectionId, sectionName, articles) => {
    setActiveSection({ id: sectionId, name: sectionName, articles })
  }, [])

  const filterBySectionAndBias = useCallback((sectionId, sectionName, articles, bias) => {
    setActiveSection({ id: sectionId, name: sectionName, articles })
    setBiasFilters([bias])
  }, [])

  const clearFilter = useCallback(() => {
    setActiveSection(null)
    setBiasFilters(['left', 'center', 'right'])
  }, [])

  // Filter by bias only (used by header BiasBars)
  const filterByBias = useCallback((bias) => {
    setActiveSection(null) // Clear section filter, show all articles
    setBiasFilters([bias])
  }, [])

  const toggleBias = useCallback((bias) => {
    setBiasFilters(prev => {
      if (prev.includes(bias)) {
        // Don't allow removing the last filter
        if (prev.length === 1) return prev
        return prev.filter(b => b !== bias)
      }
      return [...prev, bias]
    })
  }, [])

  const highlightArticle = useCallback((articleId) => {
    setHighlightedArticleId(articleId)
  }, [])

  const clearHighlight = useCallback(() => {
    setHighlightedArticleId(null)
  }, [])

  // Open mobile modal with articles
  const openMobileModal = useCallback((articles, title, initialBias = null) => {
    setMobileModalArticles(articles)
    setMobileModalTitle(title)
    if (initialBias) {
      setBiasFilters([initialBias])
    } else {
      setBiasFilters(['left', 'center', 'right'])
    }
    setMobileModalOpen(true)
  }, [])

  const closeMobileModal = useCallback(() => {
    setMobileModalOpen(false)
  }, [])

  return (
    <TopicContext.Provider
      value={{
        activeSection,
        filterBySection,
        filterBySectionAndBias,
        filterByBias,
        clearFilter,
        biasFilters,
        toggleBias,
        highlightedArticleId,
        highlightArticle,
        clearHighlight,
        subtopics,
        allArticles,
        // Mobile modal
        mobileModalOpen,
        mobileModalArticles,
        mobileModalTitle,
        openMobileModal,
        closeMobileModal,
      }}
    >
      {children}
    </TopicContext.Provider>
  )
}

export function useTopicContext() {
  const context = useContext(TopicContext)
  if (!context) {
    throw new Error('useTopicContext must be used within a TopicProvider')
  }
  return context
}

// Optional version that returns null instead of throwing
export function useTopicContextOptional() {
  return useContext(TopicContext)
}
